import { DataReader, SegmentHeaderReader, SerializationUtil, GeometryReader, CompiledGeometryReader } from "@spi/Common/Serialization";
import { SceneState, TextureAssetDescriptor } from "@spi/Engine/SceneState";
import { Light } from "../Light/Light";
import { vec3 } from "gl-matrix";
import { PathNodeGraphReader } from "./PathNodeGraphReader";
import { BoundingBox } from "@spi/Common/Util";

export class SceneStateReader extends DataReader<SceneState> {
    private bytesRead: number = 0;

    public read(data: Uint8Array): SceneState {
        // read primary header
        let util = new SerializationUtil().readFrom(this.readSegment(data).data, true);

        let reference = util.readString();
        let name = util.readString();

        let min = util.readVec3();
        let max = util.readVec3();

        let sceneState = new SceneState(reference, name);
        sceneState.boundingBox = new BoundingBox(min, max);
        let lightCount = util.readUint32();
        sceneState.lights = new Array<Light>(lightCount);
        for (let i = 0; i < lightCount; i++) {
            let lightType = util.readUint8();
            let color = util.readVec4();
            let position = util.readVec3();

            sceneState.lights[i] = new Light(lightType, position, vec3.fromValues(color[0], color[1], color[2]), color[3]);
        }

        let textureCount = util.readUint32();
        sceneState.textureAssets = new Array<TextureAssetDescriptor>(textureCount);
        for (let i = 0; i < textureCount; i++) {
            sceneState.textureAssets[i] = {
                reference: util.readString(),
                uri: util.readString(),
                translucent: util.readBoolean()
            };
        }

        let segment: SegmentData = null;
        while (segment = this.readSegment(data)) {
            switch (segment.segmentType) {
                case 1:
                    sceneState.geometryContainers.push(new GeometryReader().read(segment.data));
                    break;
                case 2:
                    sceneState.navigationGraph = new PathNodeGraphReader().read(segment.data);
                    break;
                case 3:
                    sceneState.compiledGeometry.push(new CompiledGeometryReader().read(segment.data));
                    break;
                default:
                    throw new Error(`Invalid segment ${segment.segmentType} of length ${segment.data.length}`);
            }
        }

        return sceneState;
    }

    private readSegment(data: Uint8Array): SegmentData {
        if (this.bytesRead >= data.length) {
            return null;
        }

        let reader = new SegmentHeaderReader();
        let header = reader.read(data.slice(this.bytesRead));
        this.bytesRead += reader.bytesRead;

        let segmentData = data.slice(this.bytesRead, this.bytesRead + header.size);

        this.bytesRead += header.size;

        return {
            segmentType: header.segmentType,
            data: segmentData
        };
    }
}

type SegmentData = {
    segmentType: number,
    data: Uint8Array
}